/**
 * The solo `SceneDriver` — iterations 1 + 2's free-play behaviour, moved
 * behind T13's seam and otherwise untouched. It owns exactly what `GameScene`
 * used to own directly: a `BotDriver` (timed bot turns + the claim window)
 * and the local match layer's "Next hand" (`advanceMatch`, or a plain fresh
 * deal under Simplified, which has no match wrapper).
 *
 * Nothing here is aware that an online driver exists; every method is the old
 * scene method with the same body.
 */

import { BotDriver } from './BotDriver.js';
import { LOCAL_SEAT, freshSeed, startNewGame } from './Bootstrap.js';
import { EASY_STRATEGY } from '../ai/EasyStrategy.js';
import { advanceMatch } from '../match/advanceMatch.js';
import { scoreConcludedHand } from '../match/matchScoringContext.js';
import { mulberry32, type Rng } from '../utils/prng.js';
import type { Claim } from '../game/Claims.js';
import type { HandResult } from '../game/GameState.js';
import type { Seat } from '../game/types.js';
import type { Ruleset } from '../game/ruleset/types.js';
import type { KongDecision } from '../ai/types.js';
import type { SceneDriver, SceneDriverHooks } from './sceneDrivers.js';

export interface SoloDriverOptions {
  readonly hooks: SceneDriverHooks;
  /** The ruleset the mounted hand was dealt with — scores it and deals the next one. */
  readonly ruleset: Ruleset;
  /** Classical only: the match just finished, so there is no next hand to deal. */
  readonly onMatchComplete: () => void;
}

export class SoloDriver implements SceneDriver {
  /** Solo, the human is always seat 0 — the identity case of T1's rotation. */
  readonly localSeat: Seat = LOCAL_SEAT;

  private readonly hooks: SceneDriverHooks;
  private readonly ruleset: Ruleset;
  private readonly onMatchComplete: () => void;
  private readonly rng: Rng = mulberry32(freshSeed());
  private readonly bots: BotDriver;

  constructor(opts: SoloDriverOptions) {
    this.hooks = opts.hooks;
    this.ruleset = opts.ruleset;
    this.onMatchComplete = opts.onMatchComplete;
    this.bots = new BotDriver(EASY_STRATEGY, this.rng, {
      getState: () => this.hooks.getState(),
      setState: (next) => this.hooks.setState(next),
      onLocalTurn: (canDeclareTsumo, kongOptions) =>
        this.hooks.onLocalTurn(canDeclareTsumo, kongOptions),
      onLocalClaimWindow: (claims) => this.hooks.onLocalClaimWindow(claims),
      onHandEnd: (result) => this.handleHandEnd(result),
      clearLocalUi: () => this.hooks.clearLocalUi(),
    });
  }

  start(): void {
    this.bots.advance();
  }

  discard(tileId: number): void {
    this.bots.discardLocal(tileId);
  }

  declareTsumo(): void {
    this.bots.declareLocalTsumo();
  }

  declareKong(kong: KongDecision): void {
    this.bots.declareLocalKong(kong);
  }

  resolveClaim(choice: Claim | null): void {
    this.bots.resolveLocalClaim(choice);
  }

  resolveExposedKong(): void {
    this.bots.resolveLocalExposedKong();
  }

  nextHand(): void {
    const matchState = this.hooks.getMatchState();
    if (matchState === null) {
      this.hooks.setState(startNewGame(undefined, this.ruleset));
      this.bots.advance();
      return;
    }
    const advanced = advanceMatch(matchState, this.hooks.getState(), this.ruleset, freshSeed());
    this.hooks.setMatchState(advanced.matchState);
    if (advanced.nextHand === null) {
      // Match just finished — no next hand to deal, don't restart the loop.
      this.onMatchComplete();
      return;
    }
    this.hooks.setState(advanced.nextHand);
    this.bots.advance();
  }

  setPaused(paused: boolean): void {
    this.bots.setPaused(paused);
  }

  dispose(): void {
    this.bots.dispose();
  }

  private handleHandEnd(result: HandResult): void {
    const matchState = this.hooks.getMatchState();
    const handScore =
      matchState !== null
        ? scoreConcludedHand(matchState, this.hooks.getState(), result, this.ruleset)
        : null;
    this.hooks.onHandEnd(result, handScore);
  }
}
